import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiErrorMessage, photoUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthGate } from "@/contexts/AuthGateContext";
import { Button } from "@/components/ui/button";
import MapView from "@/components/MapView";
import { BedDouble, Bath, Ruler, MapPin, Heart, Mail, MessageCircle, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1776619316276-b1b461af9f15?crop=entropy&cs=srgb&fm=jpg&w=1600";

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, formatPrice } = useLocale();
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [favSaving, setFavSaving] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/properties/${id}`).then(({ data }) => setProperty(data)).catch(() => setProperty(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!user || user.role !== "buscador") return;
    api.get("/favorites/ids").then(({ data }) => setIsFav(data.includes(id))).catch(() => {});
  }, [id, user]);

  const toggleFav = async () => {
    if (!user) return navigate("/login");
    setFavSaving(true);
    try {
      if (isFav) {
        await api.delete(`/favorites/${id}`);
        setIsFav(false);
        toast.success("Eliminado de favoritos");
      } else {
        await api.post(`/favorites/${id}`);
        setIsFav(true);
        toast.success("Guardado en favoritos");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setFavSaving(false);
    }
  };

  const startChat = () => {
    requireAuth(
      { type: "start_chat", property_id: id },
      { title: t("gate.title_chat"), sub: t("gate.sub_chat") }
    );
  };

  const whatsappUrl = property ? `https://wa.me/${(property.agent?.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(
    t("property.whatsapp_prefill").replace("{title}", property.title).replace("{id}", property.property_id)
  )}` : "";

  const contactWhatsapp = () => {
    requireAuth(
      { type: "external_url", url: whatsappUrl },
      { title: t("gate.title_whatsapp"), sub: t("gate.sub_whatsapp") }
    );
  };

  const contactEmail = () => {
    const url = `mailto:${property.agent.email}?subject=${encodeURIComponent(property.title + " · ID " + property.property_id)}`;
    requireAuth(
      { type: "external_url", url },
      { title: t("gate.title_email"), sub: t("gate.sub_email") }
    );
  };

  const revealPhone = () => {
    requireAuth(
      { type: "reveal_phone" },
      { title: t("gate.title_phone"), sub: t("gate.sub_phone") }
    );
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground">{t("common.loading")}</div>;
  if (!property) return <div className="p-16 text-center">Propiedad no encontrada</div>;

  const photos = property.photos?.length ? property.photos.map(photoUrl) : [FALLBACK];

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 pt-8 md:pt-14 pb-16">
      <Link to="/properties" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-list">
        ← {t("nav.properties")}
      </Link>

      <div className="grid lg:grid-cols-[1fr,380px] gap-10 lg:gap-16 mt-6">
        {/* LEFT: gallery + info */}
        <div>
          <div className="aspect-[16/10] overflow-hidden bg-muted rounded-sm">
            <img src={photos[activePhoto]} alt={property.title} className="h-full w-full object-cover" data-testid="detail-photo-main" />
          </div>
          {photos.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {photos.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className={`aspect-square overflow-hidden rounded-sm border ${i === activePhoto ? "border-primary" : "border-border opacity-70 hover:opacity-100"}`}
                  data-testid={`detail-photo-thumb-${i}`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10">
            <div className="label-eyebrow text-secondary">{t(`types.${property.property_type}`)} · {property.transaction_type === "venta" ? t("filters.sale") : t("filters.rent")}</div>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-[1.05]">{property.title}</h1>
            <div className="mt-3 flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-4 w-4" /> {property.address}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-6 border-y border-border py-6">
            <div>
              <div className="label-eyebrow">{t("property.bedrooms")}</div>
              <div className="mt-1 font-serif text-2xl inline-flex items-center gap-2">
                <BedDouble className="h-5 w-5 text-primary" /> {property.bedrooms}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">{t("property.bathrooms")}</div>
              <div className="mt-1 font-serif text-2xl inline-flex items-center gap-2">
                <Bath className="h-5 w-5 text-primary" /> {property.bathrooms || 0}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">{t("property.square_meters")}</div>
              <div className="mt-1 font-serif text-2xl inline-flex items-center gap-2">
                <Ruler className="h-5 w-5 text-primary" /> {property.square_meters}
              </div>
            </div>
          </div>

          {property.description && (
            <div className="mt-10">
              <div className="label-eyebrow">{t("property.description")}</div>
              <p className="mt-3 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{property.description}</p>
            </div>
          )}

          {typeof property.latitude === "number" && typeof property.longitude === "number" && (
            <div className="mt-10">
              <div className="label-eyebrow mb-3">Ubicación</div>
              <MapView properties={[property]} height="360px" />
            </div>
          )}
        </div>

        {/* RIGHT: sticky panel */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-6 border border-border p-6 md:p-8 rounded-sm bg-card">
          <div>
            <div className="label-eyebrow">{property.transaction_type === "venta" ? t("filters.sale") : t("filters.rent")}</div>
            <div className="mt-2 font-serif text-4xl text-primary">{formatPrice(property.price, property.currency)}</div>
            {property.transaction_type === "alquiler" && (
              <div className="text-xs text-muted-foreground">{t("common.per_month")}</div>
            )}
          </div>

          <div className="border-t border-border pt-6">
            <div className="label-eyebrow mb-3">{t("property.published_by")}</div>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center font-semibold">
                {property.agent?.name?.[0]?.toUpperCase() || "A"}
              </div>
              <div>
                <div className="font-medium">{property.agent?.name || "Agente"}</div>
                <div className="text-xs text-muted-foreground">{property.agent?.email}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {property.agent?.phone && (
              <Button
                type="button"
                onClick={contactWhatsapp}
                className="w-full rounded-full btn-hover-lift bg-[#25D366] hover:bg-[#1FBA57] text-white"
                data-testid="contact-whatsapp-btn"
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.891c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c6.585 0 11.946-5.335 11.949-11.896C24 8.706 22.759 5.652 20.52 3.449zM12.05 21.785h-.004c-1.784 0-3.53-.478-5.056-1.383l-.362-.214-3.759.98 1.005-3.648-.238-.372c-.993-1.573-1.517-3.392-1.516-5.267.001-5.446 4.436-9.87 9.887-9.87 2.638 0 5.117 1.024 6.982 2.882 1.865 1.858 2.893 4.328 2.891 6.958-.002 5.446-4.437 9.874-9.884 9.874z"/>
                </svg>
                {t("property.contact_whatsapp")}
              </Button>
            )}
            {property.agent?.phone && (
              user ? (
                <Button
                  asChild
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="phone-revealed-btn"
                >
                  <a href={`tel:${property.agent.phone.replace(/[^\d+]/g, "")}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    {property.agent.phone}
                  </a>
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={revealPhone}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="view-phone-btn"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {t("property.view_phone")}
                </Button>
              )
            )}
            {(!user || user.user_id !== property.agent?.user_id) && (
              <Button
                onClick={startChat}
                variant="outline"
                className="w-full rounded-full"
                data-testid="contact-chat-btn"
                disabled={chatLoading}
              >
                {chatLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
                {t("property.contact_chat")}
              </Button>
            )}
            {property.agent?.email && (
              <Button
                type="button"
                onClick={contactEmail}
                variant="ghost"
                className="w-full rounded-full"
                data-testid="contact-agent-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> {t("property.contact")}
              </Button>
            )}
            {(!user || user.role === "buscador") && (
              <Button
                variant={isFav ? "secondary" : "outline"}
                onClick={toggleFav}
                disabled={favSaving}
                className="w-full rounded-full"
                data-testid="favorite-btn"
              >
                <Heart className={`h-4 w-4 mr-2 ${isFav ? "fill-current" : ""}`} />
                {isFav ? t("property.saved") : t("property.save")}
              </Button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
